import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Day/night theme — UI_STANDARDS.md B3: honors prefers-color-scheme as the initial default (if
// the user never chose explicitly), persists an explicit choice, cross-fades the whole canvas on
// toggle (UX_MOTION.md "Theme toggle" — the actual cross-fade transition lives in tokens.css'
// `.scarlet-canvas` transition + the reduced-motion override neutralizes it automatically).
const ThemeContext = createContext(null);
const STORAGE_KEY = "fitfrik-theme";

function getInitialTheme() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.style.colorScheme = theme;
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
